import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { Network } from '@capacitor/network'
import { App } from '@capacitor/app'
import Parse from 'parse'
import TusConstructionImageUploadService from '@/services/TusConstructionImageUploadService'
import PlatformAwareUploadService from '@/services/PlatformAwareUploadService'
import { scheduleBackgroundUploadNotification } from '@/composables/useLocalNotifications'

/**
 * Composable for handling background image uploads with progress tracking,
 * queue management, retry logic, and local notifications.
 *
 * Features:
 * - Platform-aware upload routing (native background upload vs TUS)
 * - Background upload queue management
 * - Progress tracking for individual uploads
 * - Retry logic for failed uploads
 * - Local notifications for completion/failure
 * - Offline support with automatic retry when connection returns
 * - Native background uploads on iOS/Android
 * - TUS resumable uploads on web and as fallback
 */
export function useBackgroundUpload() {
  // Upload queue state
  const uploadQueue = ref([])
  const isProcessing = ref(false)
  const currentUpload = ref(null)
  
  // Upload statistics
  const stats = reactive({
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: 0
  })

  // Upload speed tracking
  const uploadStats = reactive({
    speed: 0,
    eta: 0,
    totalBytes: 0,
    uploadedBytes: 0,
    startTime: null,
    lastUpdate: null
  })

  // TUS upload controls for pause/resume
  const tusUploads = ref(new Map())
  
  // Track upload resumption data
  const uploadResumptionData = ref(new Map())

  // Maximum images allowed per upload (removed limit)
  // const MAX_IMAGES_PER_UPLOAD = 5

  // Upload states
  const UPLOAD_STATES = {
    PENDING: 'pending',
    IN_PROGRESS: 'in_progress', 
    COMPLETED: 'completed',
    FAILED: 'failed',
    RETRYING: 'retrying'
  }

  // Network status
  const isOnline = ref(true)
  
  // Computed properties
  const hasActiveUploads = computed(() => uploadQueue.value.some(upload => 
    upload.status === UPLOAD_STATES.PENDING || 
    upload.status === UPLOAD_STATES.IN_PROGRESS ||
    upload.status === UPLOAD_STATES.RETRYING
  ))

  const activeUploads = computed(() => uploadQueue.value.filter(upload => 
    upload.status === UPLOAD_STATES.PENDING || 
    upload.status === UPLOAD_STATES.IN_PROGRESS ||
    upload.status === UPLOAD_STATES.RETRYING ||
    upload.status === UPLOAD_STATES.COMPLETED
  ).map(upload => ({
    id: upload.id,
    state: upload.status === UPLOAD_STATES.IN_PROGRESS ? 'uploading' : 
           upload.status === UPLOAD_STATES.COMPLETED ? 'completed' : 
           upload.status === UPLOAD_STATES.FAILED ? 'error' : 'pending',
    progress: upload.progress || 0,
    fileName: upload.data?.images?.[0]?.name || `Upload ${upload.id.substring(8, 14)}`
  })))

  const totalProgress = computed(() => {
    if (stats.total === 0) return 0
    return Math.round((stats.completed / stats.total) * 100)
  })

  /**
   * Initialize the background upload system
   * @param {boolean} autoProcessQueue - Whether to auto-process existing queue items
   */
  async function initialize(autoProcessQueue = false) {
    try {
      // Load existing queue from localStorage
      loadQueueFromStorage()
      
      // Set up network monitoring
      const status = await Network.getStatus()
      isOnline.value = status.connected
      
      Network.addListener('networkStatusChange', (status) => {
        const wasOffline = !isOnline.value
        isOnline.value = status.connected
        
        // If we just came back online, process pending uploads
        if (wasOffline && status.connected) {
          processQueue()
        }
      })

      // Set up app state monitoring for background processing
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive && hasActiveUploads.value) {
          // App is going to background, prepare uploads for resumption
          prepareUploadsForBackground()
          processQueueInBackground()
        } else if (isActive) {
          // App is coming to foreground, resume interrupted uploads
          resumeInterruptedUploads()
        }
      })

      // Only start processing if explicitly requested
      if (autoProcessQueue && hasActiveUploads.value) {
        console.log('Auto-processing existing upload queue...')
        processQueue()
      } else if (hasActiveUploads.value) {
        console.log(`Found ${uploadQueue.value.length} items in upload queue, but auto-processing is disabled`)
      }
    } catch (error) {
      console.error('Failed to initialize background upload:', error)
    }
  }

  /**
   * Add images to upload queue
   * @param {Object} uploadData - Upload data containing images, construction, etc.
   * @returns {string} Upload ID
   */
  function addToQueue(uploadData) {
    // Validate image count
    if (uploadData.images.length === 0) {
      throw new Error('At least one image is required')
    }

    if (!uploadData.construction) {
      throw new Error('Construction site selection is required')
    }

    const uploadId = generateUploadId()
    const upload = {
      id: uploadId,
      status: UPLOAD_STATES.PENDING,
      progress: 0,
      data: { ...uploadData },
      createdAt: new Date().toISOString(),
      attempts: 0,
      maxAttempts: 3,
      error: null
    }

    uploadQueue.value.push(upload)
    stats.total += uploadData.images.length
    
    // Save to localStorage for persistence
    saveQueueToStorage()
    
    // Start processing if not already running
    if (!isProcessing.value && isOnline.value) {
      processQueue()
    }

    return uploadId
  }

  /**
   * Process the upload queue
   */
  async function processQueue() {
    if (isProcessing.value || !isOnline.value) return
    
    isProcessing.value = true
    
    try {
      while (uploadQueue.value.length > 0) {
        const pendingUpload = uploadQueue.value.find(upload => 
          upload.status === UPLOAD_STATES.PENDING || 
          upload.status === UPLOAD_STATES.RETRYING
        )
        
        if (!pendingUpload) break
        
        currentUpload.value = pendingUpload
        await processUpload(pendingUpload)
      }
    } catch (error) {
      console.error('Error processing upload queue:', error)
    } finally {
      isProcessing.value = false
      currentUpload.value = null
      saveQueueToStorage()
    }
  }

  /**
   * Process queue in background when app is backgrounded
   */
  async function processQueueInBackground() {
    // For mobile apps, we'll continue processing until the OS suspends us
    // This provides better user experience than true background tasks
    try {
      await processQueue()
    } catch (error) {
      console.error('Background processing error:', error)
    }
  }

  /**
   * Process a single upload using TUS resumable uploads
   * @param {Object} upload - Upload object
   */
  async function processUpload(upload) {
    try {
      upload.status = UPLOAD_STATES.IN_PROGRESS
      upload.progress = 0
      stats.inProgress++
      
      // Track individual image progress
      const imageProgress = new Array(upload.data.images.length).fill(0)
      let completedImages = 0

      // Initialize upload stats
      const startTime = Date.now()
      uploadStats.startTime = startTime
      uploadStats.uploadedBytes = 0
      uploadStats.totalBytes = upload.data.images.reduce((sum, img) => sum + (img.size || 1024 * 1024), 0) // estimate 1MB if size unknown

      // Perform platform-aware upload with progress tracking
      const uploadResult = await uploadWithPlatformAware(upload.data, {
        onImageProgress: (imageIndex, progress) => {
          imageProgress[imageIndex] = parseFloat(progress.percentage) || 0
          // Calculate overall progress as average of all image progress
          const totalProgress = imageProgress.reduce((sum, p) => sum + p, 0)
          upload.progress = totalProgress / upload.data.images.length
          
          // Update upload stats
          const now = Date.now()
          const elapsed = (now - startTime) / 1000 // seconds
          uploadStats.uploadedBytes = upload.progress * uploadStats.totalBytes / 100
          uploadStats.speed = elapsed > 0 ? uploadStats.uploadedBytes / elapsed : 0
          uploadStats.eta = uploadStats.speed > 0 ? (uploadStats.totalBytes - uploadStats.uploadedBytes) / uploadStats.speed : 0
          uploadStats.lastUpdate = now
        },
        onImageComplete: (imageIndex, result) => {
          completedImages++
          imageProgress[imageIndex] = 100
          upload.progress = (completedImages / upload.data.images.length) * 100
          console.log(`Image ${imageIndex + 1}/${upload.data.images.length} completed`)
        },
        onComplete: (result) => {
          console.log('All images uploaded successfully:', result)
        },
        onError: (error, imageIndex) => {
          console.error(`Error uploading image ${imageIndex}:`, error)
        }
      })

      // Store upload controls for potential cancellation
      tusUploads.value.set(upload.id, uploadResult)

      // Wait for all uploads to complete
      await Promise.allSettled(uploadResult.uploads.map(u => u.promise))

      // Check if any uploads failed
      if (uploadResult.failed.length > 0) {
        throw new Error(`${uploadResult.failed.length}/${upload.data.images.length} images failed to upload`)
      }

      // Mark as completed
      upload.status = UPLOAD_STATES.COMPLETED
      upload.progress = 100
      upload.completedAt = new Date().toISOString()
      upload.uploadResult = {
        projectId: uploadResult.completed[0]?.projectId,
        uploadedFiles: uploadResult.completed.map(r => r.filename),
        downloadUrls: uploadResult.completed.map(r => r.downloadUrl)
      }
      
      stats.completed += upload.data.images.length
      stats.inProgress--
      
      // Clean up TUS upload controls
      tusUploads.value.delete(upload.id)
      
      // Show success notification
      await showNotification({
        title: 'Upload Complete',
        body: `Successfully uploaded ${upload.data.images.length} images to ${upload.data.construction.label}`,
        id: parseInt(upload.id),
        category: 'upload_success'
      })
      
      // Remove from queue after short delay
      setTimeout(() => {
        removeFromQueue(upload.id)
      }, 8000) // Slightly longer delay to see success state
      
    } catch (error) {
      upload.attempts++
      upload.error = error.message
      stats.inProgress--
      
      // Clean up TUS upload controls on error
      const tusUpload = tusUploads.value.get(upload.id)
      if (tusUpload && tusUpload.abort) {
        tusUpload.abort()
      }
      tusUploads.value.delete(upload.id)
      
      if (upload.attempts < upload.maxAttempts) {
        // Retry with exponential backoff
        upload.status = UPLOAD_STATES.RETRYING
        const delay = Math.pow(2, upload.attempts) * 2000 // 4s, 8s, 16s (longer delays for TUS)
        
        setTimeout(() => {
          if (isOnline.value) {
            processQueue()
          }
        }, delay)
        
        await showNotification({
          title: 'Upload Retry',
          body: `Retrying upload (attempt ${upload.attempts}/${upload.maxAttempts})`,
          id: parseInt(upload.id) + 1000,
          category: 'upload_retry'
        })
      } else {
        // Mark as failed
        upload.status = UPLOAD_STATES.FAILED
        upload.failedAt = new Date().toISOString()
        stats.failed += upload.data.images.length
        
        await showNotification({
          title: 'Upload Failed',
          body: `Failed to upload images: ${error.message}`,
          id: parseInt(upload.id) + 2000,
          category: 'upload_failed'
        })
      }
    }
  }

  /**
   * Upload images using platform-aware upload service
   * @param {Object} uploadData - Upload data
   * @param {Object} callbacks - Progress and completion callbacks
   * @returns {Promise<Object>} Upload result with controls
   */
  async function uploadWithPlatformAware(uploadData, callbacks = {}) {
    try {
      console.log('BackgroundUpload: Starting platform-aware upload...')
      console.log('BackgroundUpload: Upload data contains', uploadData.images.length, 'images')
      console.log('BackgroundUpload: Construction site:', uploadData.construction.label)

      // Get platform info for logging
      const platformInfo = PlatformAwareUploadService.getPlatformInfo()
      console.log('BackgroundUpload: Platform info:', platformInfo)

      // Use platform-aware upload service
      const result = await PlatformAwareUploadService.uploadImages(uploadData, callbacks)
      console.log('BackgroundUpload: Platform-aware upload service returned result:', result)
      return result
    } catch (error) {
      // Log the full error for debugging
      console.error('BackgroundUpload: Platform-aware upload error:', error)
      console.error('BackgroundUpload: Error stack:', error.stack)
      throw new Error(`Platform-aware upload failed: ${error.message}`)
    }
  }

  /**
   * Upload images using TUS resumable uploads (legacy/fallback)
   * @param {Object} uploadData - Upload data
   * @param {Object} callbacks - Progress and completion callbacks
   * @returns {Promise<Object>} Upload result with controls
   */
  async function uploadWithTus(uploadData, callbacks = {}) {
    try {
      // Validate TUS server configuration
      const tusServerUrl = import.meta.env.VITE_TUS_SERVER_URL
      if (!tusServerUrl) {
        throw new Error('TUS_SERVER_URL not configured. Please check environment settings.')
      }

      console.log('TUS: Starting upload with server:', tusServerUrl)
      console.log('TUS: Upload data contains', uploadData.images.length, 'images')
      console.log('TUS: Construction site:', uploadData.construction.label)

      // Use TUS upload service
      const result = await TusConstructionImageUploadService.uploadImages(uploadData, callbacks)
      console.log('TUS: Upload service returned result:', result)
      return result
    } catch (error) {
      // Log the full error for debugging
      console.error('TUS: Upload error details:', error)
      console.error('TUS: Error stack:', error.stack)
      throw new Error(`TUS upload failed: ${error.message}`)
    }
  }

  /**
   * Retry a failed upload (with TUS support)
   * @param {string} uploadId - Upload ID to retry
   */
  function retryUpload(uploadId) {
    const upload = uploadQueue.value.find(u => u.id === uploadId)
    if (upload && upload.status === UPLOAD_STATES.FAILED) {
      upload.status = UPLOAD_STATES.PENDING
      upload.attempts = 0
      upload.error = null
      upload.progress = 0
      stats.failed -= upload.data.images.length
      
      // Clean up any existing TUS upload references
      if (tusUploads.value.has(uploadId)) {
        tusUploads.value.delete(uploadId)
      }
      
      if (isOnline.value) {
        processQueue()
      }
    }
  }

  /**
   * Cancel an upload (with TUS support)
   * @param {string} uploadId - Upload ID to cancel
   */
  function cancelUpload(uploadId) {
    const uploadIndex = uploadQueue.value.findIndex(u => u.id === uploadId)
    if (uploadIndex !== -1) {
      const upload = uploadQueue.value[uploadIndex]
      
      // Cancel TUS uploads if in progress
      const tusUpload = tusUploads.value.get(uploadId)
      if (tusUpload && tusUpload.abort) {
        console.log('Cancelling TUS uploads for:', uploadId)
        tusUpload.abort()
        tusUploads.value.delete(uploadId)
      }
      
      if (upload.status === UPLOAD_STATES.IN_PROGRESS) {
        upload.status = UPLOAD_STATES.FAILED
        upload.error = 'Cancelled by user'
        stats.inProgress--
      }
      
      // Remove from queue
      removeFromQueue(uploadId)
    }
  }

  /**
   * Remove upload from queue
   * @param {string} uploadId - Upload ID to remove
   */
  function removeFromQueue(uploadId) {
    const uploadIndex = uploadQueue.value.findIndex(u => u.id === uploadId)
    if (uploadIndex !== -1) {
      const upload = uploadQueue.value[uploadIndex]
      
      // Update stats
      if (upload.status === UPLOAD_STATES.PENDING || upload.status === UPLOAD_STATES.RETRYING) {
        stats.total -= upload.data.images.length
      } else if (upload.status === UPLOAD_STATES.FAILED) {
        stats.failed -= upload.data.images.length
      }
      
      uploadQueue.value.splice(uploadIndex, 1)
      saveQueueToStorage()
    }
  }

  /**
   * Clear all completed uploads from queue
   */
  function clearCompletedUploads() {
    const completedUploads = uploadQueue.value.filter(u => u.status === UPLOAD_STATES.COMPLETED)
    completedUploads.forEach(upload => {
      removeFromQueue(upload.id)
    })
  }

  /**
   * Get upload by ID
   * @param {string} uploadId - Upload ID
   * @returns {Object|undefined} Upload object
   */
  function getUpload(uploadId) {
    return uploadQueue.value.find(u => u.id === uploadId)
  }

  /**
   * Show local notification
   * @param {Object} notification - Notification options
   */
  async function showNotification(notification) {
    try {
      // Determine if this is EKKKO brand
      const isEkkko = window.location.hostname.includes('ekkko') || 
                     localStorage.getItem('isEkkko') === 'true' ||
                     import.meta.env.VITE_IS_EKKKO === 'true'

      const success = await scheduleBackgroundUploadNotification({
        title: notification.title,
        body: notification.body,
        id: notification.id,
        category: notification.category,
        isEkkko
      })
      
      if (!success) {
        console.log('Notification not shown (permissions not granted or not supported)')
      }
    } catch (error) {
      console.warn('Failed to show notification:', error.message)
    }
  }

  /**
   * Generate unique upload ID
   * @returns {string} Upload ID
   */
  function generateUploadId() {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Save queue to localStorage for persistence
   */
  function saveQueueToStorage() {
    try {
      const queueData = {
        queue: uploadQueue.value,
        stats: stats,
        timestamp: Date.now()
      }
      localStorage.setItem('backgroundUploadQueue', JSON.stringify(queueData))
    } catch (error) {
      console.warn('Failed to save upload queue to storage:', error)
    }
  }

  /**
   * Load queue from localStorage
   */
  function loadQueueFromStorage() {
    try {
      const stored = localStorage.getItem('backgroundUploadQueue')
      if (stored) {
        const queueData = JSON.parse(stored)
        
        // Only load recent data (within 24 hours)
        const dayAgo = Date.now() - (24 * 60 * 60 * 1000)
        if (queueData.timestamp > dayAgo) {
          uploadQueue.value = queueData.queue || []
          Object.assign(stats, queueData.stats || {})
          
          // Reset in-progress uploads to pending and prepare for resumption
          uploadQueue.value.forEach(upload => {
            if (upload.status === UPLOAD_STATES.IN_PROGRESS) {
              upload.status = UPLOAD_STATES.PENDING
              upload.progress = 0
              upload.canResume = true // Mark as resumable
              upload.interruptedAt = new Date().toISOString()
            }
          })
        }
      }
    } catch (error) {
      console.warn('Failed to load upload queue from storage:', error)
    }
  }


  /**
   * Pause/Resume specific upload (TUS feature)
   * @param {string} uploadId - Upload ID
   * @param {boolean} pause - Whether to pause (true) or resume (false)
   */
  function pauseResumeUpload(uploadId, pause = true) {
    const tusUpload = tusUploads.value.get(uploadId)
    const upload = uploadQueue.value.find(u => u.id === uploadId)
    
    if (tusUpload && upload) {
      if (pause) {
        // TUS client doesn't have a built-in pause, but we can abort and set to pending
        tusUpload.abort()
        upload.status = UPLOAD_STATES.PENDING
        upload.error = 'Paused by user'
        console.log('Upload paused:', uploadId)
      } else {
        // Resume by reprocessing
        upload.status = UPLOAD_STATES.PENDING
        upload.error = null
        processQueue()
        console.log('Upload resumed:', uploadId)
      }
    }
  }

  /**
   * Get upload statistics including platform info
   * @returns {Object} Upload statistics including platform and TUS info
   */
  function getUploadStats() {
    return {
      activeUploads: tusUploads.value.size,
      tusServerUrl: import.meta.env.VITE_TUS_SERVER_URL,
      platformInfo: PlatformAwareUploadService.getPlatformInfo(),
      ...TusConstructionImageUploadService.getUploadStats()
    }
  }

  /**
   * Get TUS upload statistics (legacy)
   * @returns {Object} Upload statistics including TUS info
   */
  function getTusStats() {
    return getUploadStats()
  }

  /**
   * Test platform capabilities for uploads
   * @returns {Promise<Object>} Platform capabilities
   */
  async function testPlatformCapabilities() {
    try {
      const capabilities = await PlatformAwareUploadService.testPlatformCapabilities()
      console.log('BackgroundUpload: Platform capabilities tested:', capabilities)
      return capabilities
    } catch (error) {
      console.error('BackgroundUpload: Failed to test platform capabilities:', error)
      return {
        platform: 'unknown',
        isNative: false,
        backgroundUpload: false,
        tusUpload: true,
        parseUpload: !!window.Parse,
        error: error.message
      }
    }
  }

  /**
   * Prepare uploads for background processing
   */
  function prepareUploadsForBackground() {
    console.log('Preparing uploads for background processing...')
    
    // Store current upload states for resumption
    uploadQueue.value.forEach(upload => {
      if (upload.status === UPLOAD_STATES.IN_PROGRESS) {
        uploadResumptionData.value.set(upload.id, {
          uploadId: upload.id,
          progress: upload.progress,
          data: upload.data,
          timestamp: Date.now(),
          canResume: true
        })
      }
    })
    
    // Save resumption data to localStorage
    saveResumptionDataToStorage()
  }

  /**
   * Resume interrupted uploads after app becomes active
   */
  async function resumeInterruptedUploads() {
    console.log('Checking for interrupted uploads to resume...')
    
    // Load resumption data from storage
    loadResumptionDataFromStorage()
    
    let resumedCount = 0
    
    // Check for uploads that can be resumed
    for (const upload of uploadQueue.value) {
      if (upload.canResume && upload.status === UPLOAD_STATES.PENDING) {
        const resumptionData = uploadResumptionData.value.get(upload.id)
        
        if (resumptionData) {
          console.log(`Attempting to resume upload: ${upload.id}`)
          
          // Mark as retrying instead of pending
          upload.status = UPLOAD_STATES.RETRYING
          upload.error = 'Resuming after interruption'
          
          resumedCount++
        }
      }
    }
    
    if (resumedCount > 0) {
      console.log(`Resumed ${resumedCount} interrupted uploads`)
      // Start processing the queue to handle resumed uploads
      if (isOnline.value && !isProcessing.value) {
        processQueue()
      }
    }
  }

  /**
   * Save upload resumption data to localStorage
   */
  function saveResumptionDataToStorage() {
    try {
      const resumptionArray = Array.from(uploadResumptionData.value.entries())
      localStorage.setItem('tusUploadResumptionData', JSON.stringify({
        data: resumptionArray,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.warn('Failed to save resumption data:', error)
    }
  }

  /**
   * Load upload resumption data from localStorage
   */
  function loadResumptionDataFromStorage() {
    try {
      const stored = localStorage.getItem('tusUploadResumptionData')
      if (stored) {
        const { data, timestamp } = JSON.parse(stored)
        
        // Only load recent data (within 2 hours)
        const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000)
        if (timestamp > twoHoursAgo) {
          uploadResumptionData.value = new Map(data)
        } else {
          // Clean up old data
          localStorage.removeItem('tusUploadResumptionData')
        }
      }
    } catch (error) {
      console.warn('Failed to load resumption data:', error)
    }
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    // Prepare uploads for background before cleanup
    if (hasActiveUploads.value) {
      prepareUploadsForBackground()
    }
    
    // Abort all active TUS uploads
    tusUploads.value.forEach((tusUpload, uploadId) => {
      if (tusUpload && tusUpload.abort) {
        tusUpload.abort()
      }
    })
    tusUploads.value.clear()
    uploadResumptionData.value.clear()
    
    // Clean up TUS URL storage
    TusConstructionImageUploadService.cleanupExpiredUrls()
    
    Network.removeAllListeners()
    App.removeAllListeners()
  }

  // Auto-initialize when composable is used (with resumption support)
  onMounted(() => {
    initialize(true) // Enable auto-processing to handle resumed uploads
  })

  onUnmounted(() => {
    cleanup()
  })

  return {
    // State
    uploadQueue: computed(() => uploadQueue.value),
    isProcessing: computed(() => isProcessing.value),
    currentUpload: computed(() => currentUpload.value),
    stats: computed(() => stats),
    uploadStats: computed(() => uploadStats),
    isOnline: computed(() => isOnline.value),
    hasActiveUploads,
    activeUploads,
    totalProgress,
    
    // Actions
    addToQueue,
    retryUpload,
    cancelUpload,
    removeFromQueue,
    clearCompletedUploads,
    getUpload,
    processQueue,
    initialize,
    pauseResumeUpload,
    getTusStats,
    getUploadStats,
    testPlatformCapabilities,

    // Constants
    UPLOAD_STATES
  }
}